import React, { useState, useEffect } from 'react';
// import PropTypes from 'prop-types'; // Import PropTypes
import { db } from '../firebase-config'; // Your Firebase config
import { getDocs, collection } from 'firebase/firestore';
import { useAuth } from '../useAuth'; // Import your useAuth hook
import { getIdTokenResult } from 'firebase/auth'; // Import function to get token result
import { ColorRing } from 'react-loader-spinner'; // Optional: For loading state
import { Link } from 'react-router-dom'; // For linking back
import { // Import icons from Heroicons
    ShieldExclamationIcon, // For Access Denied
    ChartBarIcon,          // For Total Reviews
    StarIcon as StarIconSolid, // For Average Rating Stat
    // TableCellsIcon        // If needed for table header
} from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline'; // For empty stars

/**
 * Renders the Admin Reviews Dashboard page.
 * Fetches and displays user reviews only if the current user is authenticated
 * and has the 'admin' value within their 'globalClaim' custom claim array.
 *
 * @component
 * @example
 * return <Reviews />
 */
const Reviews = () => {
    const { currentUser } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoadingAuth, setIsLoadingAuth] = useState(true);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [fetchError, setFetchError] = useState(null); // State for fetch errors

    useEffect(() => {
        document.title = "Admin Reviews - ProRecruit";
        return () => {
            document.title = "ProRecruit";
        };
    }, []);

    // Check authentication and admin claim
    useEffect(() => {
        const checkAdminStatus = async () => {
            setIsLoadingAuth(true);
            setFetchError(null); // Reset error on user change
            if (currentUser) {
                try {
                    const idTokenResult = await getIdTokenResult(currentUser, false); // Consider forcing refresh (true) if claims update frequently
                    const claims = idTokenResult.claims;
                    console.debug("User Claims:", claims);

                    if (claims.globalClaim && Array.isArray(claims.globalClaim) && claims.globalClaim.includes('admin')) {
                        setIsAdmin(true);
                        console.info("User verified as Admin.");
                    } else {
                        setIsAdmin(false);
                        console.warn("User is not Admin. globalClaim value:", claims.globalClaim);
                    }
                } catch (error) {
                    console.error("Error fetching custom claims:", error);
                    setIsAdmin(false);
                    setFetchError("Error verifying admin status."); // Set error state
                }
            } else {
                setIsAdmin(false);
                console.info("User not logged in.");
            }
            setIsLoadingAuth(false);
        };

        checkAdminStatus();
    }, [currentUser]);

    // Fetch reviews if admin status is confirmed
    useEffect(() => {
        if (isLoadingAuth || !isAdmin) {
            setReviews([]);
            return;
        }

        const fetchReviews = async () => {
            setIsLoadingData(true);
            setFetchError(null); // Reset fetch error
            try {
                // IMPORTANT: Ensure Firestore rules protect this collection
                const querySnapshot = await getDocs(collection(db, "reviews"));
                const fetchedReviews = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setReviews(fetchedReviews);
            } catch (error) {
                console.error("Error fetching reviews:", error);
                setFetchError("Failed to load reviews. Please try again later."); // Set fetch error state
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchReviews();
    }, [isAdmin, isLoadingAuth]); // Rerun if admin status changes or auth loading finishes

    // --- Calculations ---
    const totalCount = reviews.length;
    const totalRating = reviews.reduce((acc, review) => acc + (review.rating || 0), 0);
    const averageRating = totalCount > 0 ? totalRating / totalCount : 0;

    // --- Star Rendering ---
    const renderStars = (rating) => {
        const normalizedRating = Math.min(Math.max(rating, 0), 5); // Clamp between 0-5
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                i <= normalizedRating
                    ? <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" />
                    : <StarIconOutline key={i} className="h-5 w-5 text-gray-300" />
            );
        }
        return stars;
    };

    // --- Loading States ---
    if (isLoadingAuth) {
        return (
            <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
                <ColorRing visible={true} height="80" width="80" ariaLabel="loading-auth" />
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">Verifying Access...</p>
            </div>
        );
    }

    // --- Access Denied State ---
    if (!isAdmin) {
        return (
            <div className="flex flex-col justify-center items-center h-screen text-center p-6 bg-gray-100">
                <ShieldExclamationIcon className="h-16 w-16 text-red-500 mb-4" />
                <h1 className="text-2xl font-semibold text-slate-900 font-bold tracking-tight mb-2">Access Denied</h1>
                <p className="text-slate-600 leading-relaxed mb-6">You do not have the necessary permissions to view this page.</p>
                <Link to="/dashboard" className="px-4 py-2 bg-[#A47713] hover:bg-[#8a6310] transition duration-150 ease-in-out">
                    Go to Dashboard
                </Link>
            </div>
        );
    }

    // --- Admin Dashboard Render ---
    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold text-slate-900 font-bold tracking-tight mb-8">User Reviews Dashboard</h1>

                {/* Error Display */}
                 {fetchError && (
                    <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded" role="alert">
                        <p className="font-bold">Error</p>
                        <p>{fetchError}</p>
                    </div>
                 )}

                {/* Summary Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                    {/* Total Reviews Card */}
                    <div className="bg-white p-6 rounded-xl shadow-md flex items-center space-x-4">
                         <div className="flex-shrink-0 bg-blue-100 rounded-full p-3">
                            <ChartBarIcon className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-500 truncate">Total Reviews</p>
                            <p className="mt-1 text-3xl font-semibold text-slate-900 font-bold tracking-tight">{totalCount}</p>
                        </div>
                    </div>
                    {/* Average Rating Card */}
                    <div className="bg-white p-6 rounded-xl shadow-md flex items-center space-x-4">
                         <div className="flex-shrink-0 bg-yellow-100 rounded-full p-3">
                             <StarIconSolid className="h-6 w-6 text-yellow-500" />
                         </div>
                        <div>
                            <p className="text-sm font-medium text-slate-500">Average Rating</p>
                            <div className="flex items-baseline mt-1">
                                <p className="text-3xl font-semibold text-slate-900 font-bold tracking-tight mr-2">{averageRating.toFixed(1)}</p>
                                <div className="flex">{renderStars(averageRating)}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reviews Table */}
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <h2 className="text-xl font-semibold text-slate-900 font-bold tracking-tight px-6 py-4 border-b border-gray-200">
                        All Reviews
                    </h2>
                    {isLoadingData ? (
                        <div className="flex justify-center items-center py-16">
                            <ColorRing visible={true} height="60" width="60" ariaLabel="loading-data" />
                            <p className="ml-3 text-slate-500">Loading reviews...</p>
                        </div>
                    ) : reviews.length === 0 && !fetchError ? ( // Check for error before showing "No reviews"
                        <p className="text-center text-slate-500 py-16 px-6">No reviews have been submitted yet.</p>
                    ) : !fetchError && ( // Only render table if no fetch error
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Contact</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Rating</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Review</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {reviews.map((review) => (
                                        <tr key={review.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 font-bold tracking-tight">{review.fullName || 'Anonymous'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                                <div>{review.email || 'N/A'}</div>
                                                <div>{review.phoneNumber || ''}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                                <div className="flex items-center">
                                                    <span className="mr-1.5">{review.rating ? review.rating.toFixed(1) : 'N/A'}</span>
                                                    {renderStars(review.rating || 0)}
                                                </div>
                                            </td>
                                            {/* Use max-w-xs or similar and truncate for long reviews */}
                                            <td className="px-6 py-4 text-sm text-slate-500 max-w-xs truncate" title={review.review || ''}>
                                                {review.review || 'N/A'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Define PropTypes for the component
Reviews.propTypes = {
    // No external props currently, but good practice to keep if props are added later
};


export default Reviews;